import type {
  AbsenceEntry,
  AbsenceListResponse,
  AddSubstituteResponse,
} from '@league/shared/contract';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { Button, ErrorNote, Input } from '../../components/ui';
import { ApiError, apiDelete, apiFetch, apiPost } from '../../lib/api';

/**
 * WHO'S PLAYING TONIGHT — the first thing on the seating page, before the tables are arranged
 * (owner, 2026-10-13: "Logically, do I want to see the list of people … and then arrange the
 * tables?"). One list of the roster, each with a tick. Untick somebody who isn't coming and a Sub
 * choice appears under their name: no sub, a sub from the city's list, or someone new by name. Tick
 * them again to undo. It replaces four scattered controls — "Mark a player absent", "Mark attending",
 * a row of every sub as a button, and "+ New sub".
 *
 * An absence belongs to a SESSION, not to a league: the same player is away on week two and there on
 * week three. A sub is somebody in the city's list; someone new is added to it in the same step.
 */

interface SubOption {
  player: { id: string; firstName: string; lastName: string };
  substitute: { active: boolean };
}

interface Person {
  enrollmentId: string;
  name: string;
  firstName?: string;
  lastName?: string;
  status?: string;
}

const NEW_SUB = '__new__';

export function Absences({
  sessionId,
  marketId,
  playing,
  onChanged,
}: {
  sessionId: string;
  marketId: string | null;
  playing: Person[];
  onChanged: () => void;
}) {
  const queryClient = useQueryClient();
  const [expanded, setExpanded] = useState(false);
  const [addingFor, setAddingFor] = useState<string | null>(null);

  const absences = useQuery({
    refetchInterval: 30000,
    refetchOnWindowFocus: true,
    queryKey: ['absences', sessionId],
    queryFn: () => apiFetch<AbsenceListResponse>(`/api/sessions/${sessionId}/absences`),
  });
  const subs = useQuery({
    queryKey: ['substitutes', marketId],
    queryFn: () => apiFetch<{ substitutes: SubOption[] }>(`/api/markets/${marketId}/substitutes`),
    enabled: Boolean(marketId),
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['absences', sessionId] });
    void queryClient.invalidateQueries({ queryKey: ['substitutes', marketId] });
    void queryClient.invalidateQueries({ queryKey: ['league-subs'] });
    void queryClient.invalidateQueries({ queryKey: ['director-dashboard'] });
    void queryClient.invalidateQueries({ queryKey: ['director-next-steps'] });
    void queryClient.invalidateQueries({ queryKey: ['tasks'] });
    onChanged();
  };

  const record = useMutation({
    mutationFn: (body: { absentEnrollmentId: string; subPlayerId?: string | null }) =>
      apiPost(`/api/sessions/${sessionId}/absences`, body),
    onSuccess: refresh,
  });
  const clear = useMutation({
    mutationFn: (absenceId: string) => apiDelete(`/api/absences/${absenceId}`),
    onSuccess: refresh,
  });

  const away = absences.data?.absences ?? [];
  const awayByEnrollment = new Map(away.map((entry) => [entry.absence.absentEnrollmentId, entry]));
  const pool = (subs.data?.substitutes ?? [])
    .filter((s) => s.substitute.active)
    .sort(
      (a, b) =>
        a.player.firstName.localeCompare(b.player.firstName) ||
        a.player.lastName.localeCompare(b.player.lastName),
    );
  /* A sub already covering somebody else tonight is not offered twice. */
  const covering = new Set(
    away.flatMap((entry) => (entry.absence.subPlayerId ? [entry.absence.subPlayerId] : [])),
  );

  type Row = { enrollmentId: string; name: string; sort: string; entry: AbsenceEntry | null };
  const rows: Row[] = [
    ...playing
      .filter(
        (p) => (!p.status || p.status === 'enrolled') && !awayByEnrollment.has(p.enrollmentId),
      )
      .map((p) => ({
        enrollmentId: p.enrollmentId,
        name: p.name,
        sort: `${p.firstName ?? p.name} ${p.lastName ?? ''}`,
        entry: null,
      })),
    ...away.map((entry) => ({
      enrollmentId: entry.absence.absentEnrollmentId,
      name: `${entry.player.firstName} ${entry.player.lastName}`,
      sort: `${entry.player.firstName} ${entry.player.lastName}`,
      entry,
    })),
  ].sort((a, b) => a.sort.localeCompare(b.sort));

  const here = rows.filter((r) => !r.entry).length;
  const subbing = away.filter((entry) => entry.sub).length;
  const busy = record.isPending || clear.isPending;
  const error = record.error ?? clear.error;

  return (
    <section
      aria-labelledby="tonight-heading"
      className="overflow-clip rounded-[14px] border border-light bg-paper"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-light px-4 py-3">
        <h2 id="tonight-heading" className="font-display text-xl">
          <button type="button" aria-expanded={expanded} aria-controls="tonight-players" className="min-h-11 text-left focus-visible:outline focus-visible:outline-2" onClick={() => setExpanded(!expanded)}>
            Who’s playing tonight <span aria-hidden="true">{expanded ? '▴' : '▾'}</span>
          </button>
        </h2>
        <p className="tabular text-[14px] text-ink-2">
          {here + subbing} playing{away.length ? ` · ${away.length} away` : ''}
          {subbing ? ` · ${subbing} ${subbing === 1 ? 'sub' : 'subs'}` : ''}
        </p>
      </div>
      {error ? (
        <div className="px-4 pt-3">
          <ErrorNote>
            {error instanceof ApiError ? error.message : 'Could not save that.'}
          </ErrorNote>
        </div>
      ) : null}
      {absences.isError ? <p role="alert" className="px-4 py-2">Could not load attendance. <button type="button" className="underline" onClick={() => void absences.refetch()}>Try again</button></p> : null}
      {away.length ? <ul className="px-4 py-2 text-sm text-ink-2" aria-label="Absence notices">
        {away.map(entry => <li key={entry.absence.id} className="py-1">
          {entry.player.firstName} {entry.player.lastName} is away{entry.sub ? ` · ${entry.sub.firstName} ${entry.sub.lastName} covering` : entry.absence.subPlan === 'league' ? ' · asked you to find a sub' : entry.absence.subPlan === 'own' ? ' · finding their own sub' : ''}.
          {entry.seatNeedsAttention ? <> Table {entry.tableNumber} still has their seat. <Button size="inline" variant="quiet" disabled={busy} onClick={() => record.mutate({ absentEnrollmentId: entry.absence.absentEnrollmentId, subPlayerId: entry.absence.subPlayerId })}>Update attendance</Button></> : null}
        </li>)}
      </ul> : null}
      <ul id="tonight-players" hidden={!expanded}>
        {rows.map((row) => {
          const entry = row.entry;
          const chosen = entry?.absence.subPlayerId ?? '';
          return (
            <li key={row.enrollmentId} className="border-b border-light px-4 last:border-b-0">
              <div className="flex min-h-12 flex-wrap items-center gap-x-5 gap-y-1 py-1">
                <label className="flex min-h-11 items-center gap-3 text-[15px]">
                  <input
                    type="checkbox"
                    className="size-5 accent-deep"
                    checked={!entry}
                    disabled={busy}
                    onChange={(e) => {
                      if (e.target.checked && entry) clear.mutate(entry.absence.id);
                      else if (!e.target.checked)
                        record.mutate({ absentEnrollmentId: row.enrollmentId });
                    }}
                  />
                  <span className={entry ? 'text-ink-3 line-through' : 'font-semibold text-ink'}>
                    {row.name}
                  </span>
                </label>
                {entry ? (
                  <label className="flex items-center gap-2 text-[14px]">
                    <span className="text-ink-2">Sub</span>
                    <select
                      value={addingFor === row.enrollmentId ? NEW_SUB : chosen}
                      disabled={busy}
                      onChange={(e) => {
                        if (e.target.value === NEW_SUB) {
                          setAddingFor(row.enrollmentId);
                          return;
                        }
                        setAddingFor(null);
                        record.mutate({
                          absentEnrollmentId: row.enrollmentId,
                          subPlayerId: e.target.value || null,
                        });
                      }}
                      className="min-h-10 rounded-md border border-control-border bg-paper px-3 text-[14px] focus-visible:border-deep"
                    >
                      <option value="">No sub</option>
                      {pool
                        .filter(
                          (option) =>
                            option.player.id === chosen || !covering.has(option.player.id),
                        )
                        .map((option) => (
                          <option key={option.player.id} value={option.player.id}>
                            {option.player.firstName} {option.player.lastName}
                          </option>
                        ))}
                      {marketId ? <option value={NEW_SUB}>Someone new…</option> : null}
                    </select>
                  </label>
                ) : null}
              </div>
              {entry && addingFor === row.enrollmentId && marketId ? (
                <div className="pb-2">
                  <NewSub
                    marketId={marketId}
                    sessionId={sessionId}
                    onCancel={() => setAddingFor(null)}
                    onAdded={(playerId) => {
                      setAddingFor(null);
                      record.mutate({
                        absentEnrollmentId: row.enrollmentId,
                        subPlayerId: playerId,
                      });
                    }}
                  />
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/** Someone new: first and last name, email if there is one. Added to the city's subs. */
function NewSub({
  marketId,
  sessionId,
  onAdded,
  onCancel,
}: {
  marketId: string;
  /* Named so the server takes the market from tonight's session — how an instructor adds a walk-in (2026-10-16). */
  sessionId: string;
  onAdded: (playerId: string) => void;
  onCancel: () => void;
}) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const add = useMutation({
    mutationFn: () =>
      apiPost<AddSubstituteResponse>('/api/substitutes', {
        marketId,
        sessionId,
        newPlayer: {
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          ...(email.trim() ? { email: email.trim() } : {}),
        },
      }),
    onSuccess: ({ substitute }) => onAdded(substitute.playerId),
  });
  return (
    <form
      className="flex flex-wrap items-end gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        add.mutate();
      }}
    >
      <label className="flex flex-col gap-1 text-[13px] font-semibold">
        First name
        <Input
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          className="w-40"
          autoFocus
        />
      </label>
      <label className="flex flex-col gap-1 text-[13px] font-semibold">
        Last name
        <Input value={lastName} onChange={(e) => setLastName(e.target.value)} className="w-40" />
      </label>
      <label className="flex flex-col gap-1 text-[13px] font-semibold">
        Email (optional)
        <Input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-56"
        />
      </label>
      <Button
        type="submit"
        size="inline"
        disabled={add.isPending || !firstName.trim() || !lastName.trim()}
      >
        {add.isPending ? 'Adding…' : 'Add'}
      </Button>
      <Button type="button" variant="secondary" size="inline" onClick={onCancel}>
        Cancel
      </Button>
      {add.isError ? (
        <span role="alert" className="w-full text-[13px] text-rust">
          {add.error instanceof ApiError ? add.error.message : 'Could not add that sub.'}
        </span>
      ) : null}
    </form>
  );
}
